import { Button, ButtonProps } from '@chakra-ui/react';
import { forwardRef, ReactNode } from 'react';

/*
 * v2 shipped `isLoading`/`isDisabled`; v3's real `Button` only understands
 * `loading`/`disabled`. This was a raw, untranslated passthrough
 * (`const MButton = Button`) until now — every `isLoading`/`isDisabled`
 * passed to it was a silent no-op (button stayed clickable while "loading",
 * never actually disabled). Real `loading`/`disabled` still work directly
 * and take precedence if a caller somehow passes both.
 *
 * v2's `Button` also took `leftIcon`/`rightIcon`/`iconSpacing`; v3's real
 * `Button` has no icon slots at all (children only). Rendered as plain
 * siblings of `children` — the size variants in `Button.ts` each set their
 * own `gap`, inherited from v3's built-in recipe, so icon-to-text spacing
 * still comes from the theme. `iconSpacing` overrides that `gap` directly
 * for the 2 call sites that customize it.
 *
 * Only wraps `children` in a fragment with the icons when at least one icon
 * is actually passed — `asChild` (used for `<MButton asChild><Link .../>
 * </MButton>`-style polymorphism, v3's replacement for the removed `as`
 * prop) requires its child to be a single valid element
 * (`React.isValidElement`), which an array/fragment of siblings — even one
 * containing only `undefined` icon slots — fails, silently rendering
 * nothing. Passing `children` through directly when there's no icon keeps
 * `asChild` working for the common (no-icon) case.
 */
export interface MButtonProps extends ButtonProps {
  isLoading?: boolean;
  isDisabled?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  iconSpacing?: ButtonProps['gap'];
}

const MButton = forwardRef<HTMLButtonElement, MButtonProps>(
  (
    {
      isLoading,
      isDisabled,
      leftIcon,
      rightIcon,
      iconSpacing,
      loading,
      disabled,
      gap,
      children,
      ...rest
    },
    ref,
  ) => (
    <Button
      ref={ref}
      loading={loading ?? isLoading}
      disabled={disabled ?? isDisabled}
      gap={gap ?? iconSpacing}
      {...rest}
    >
      {leftIcon || rightIcon ? (
        <>
          {leftIcon}
          {children}
          {rightIcon}
        </>
      ) : (
        children
      )}
    </Button>
  ),
);

MButton.displayName = 'MButton';

export default MButton;
